import { createTheme } from '@mui/material/styles';

// SmartAI Studio Color Palette
const colors = {
  // Primary Colors
  coralRed: '#E16565',
  coralRedDark: '#D74A49',
  tealLight: '#4DD0B8',
  tealDark: '#2B9EB3',
  
  // Backgrounds
  darkBackground: '#2C2D2F',
  darkBackgroundPaper: '#3A3B3D',
  lightGrey: '#E5E5E5',
  lightBackground: '#F5F5F5',
  
  // Supporting Colors
  mutedTeal: '#8BA0A4',
  mutedTealDark: '#7A9A9F',
  lightCoral: '#F5A5A5',
  
  // Text Colors
  textPrimary: '#FFFFFF',
  textSecondary: '#E5E5E5',
  textDark: '#000000',
  textMuted: '#B0B0B0',
};

// Create SmartAI theme
export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.coralRed,
      dark: colors.coralRedDark,
      light: colors.lightCoral,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: colors.tealDark,
      light: colors.tealLight,
      dark: colors.mutedTealDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: colors.darkBackground,
      paper: colors.darkBackgroundPaper,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    error: {
      main: '#FF6B6B',
    },
    warning: {
      main: '#FFD93D',
    },
    info: {
      main: colors.tealLight,
    },
    success: {
      main: '#51CF66',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    body1: {
      lineHeight: 1.7,
    },
    body2: {
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${colors.mutedTeal} ${colors.darkBackgroundPaper}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: colors.mutedTeal,
            border: `2px solid ${colors.darkBackgroundPaper}`,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: colors.darkBackgroundPaper,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '10px 24px',
          transition: 'all 0.2s ease-in-out',
          minHeight: 44, // Touch-friendly minimum height
          '@media (max-width: 600px)': {
            fontSize: '0.875rem',
            padding: '8px 16px',
          },
        },
        containedPrimary: {
          background: `linear-gradient(45deg, ${colors.coralRedDark} 30%, ${colors.coralRed} 90%)`,
          boxShadow: '0 3px 5px 2px rgba(225, 101, 101, .3)',
          '&:hover': {
            background: `linear-gradient(45deg, ${colors.coralRedDark} 60%, ${colors.coralRed} 100%)`,
            boxShadow: '0 4px 8px 3px rgba(225, 101, 101, .4)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(45deg, ${colors.tealDark} 30%, ${colors.tealLight} 90%)`,
          boxShadow: '0 3px 5px 2px rgba(77, 208, 184, .3)',
          '&:hover': {
            background: `linear-gradient(45deg, ${colors.tealDark} 60%, ${colors.tealLight} 100%)`,
            boxShadow: '0 4px 8px 3px rgba(77, 208, 184, .4)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: colors.darkBackgroundPaper,
          backgroundImage: 'none',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: colors.mutedTeal,
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: colors.tealLight,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.tealLight,
              borderWidth: 2,
            },
          },
          '@media (max-width: 600px)': {
            '& .MuiInputBase-root': {
              fontSize: '0.875rem',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.darkBackgroundPaper,
          backgroundImage: 'none',
          borderBottom: `1px solid ${colors.mutedTeal}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.tealLight,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44, // Touch-friendly minimum size
          minHeight: 44,
          '@media (max-width: 600px)': {
            padding: 12,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 48, // Touch-friendly list items
          '@media (max-width: 600px)': {
            paddingTop: 12,
            paddingBottom: 12,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48, // Touch-friendly tabs
          '@media (max-width: 600px)': {
            fontSize: '0.875rem',
            minWidth: 100,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: 16,
            fontSize: '1.125rem',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: 16,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: 16,
            flexDirection: 'column-reverse',
            '& > :not(:first-of-type)': {
              marginLeft: 0,
              marginBottom: 8,
            },
          },
        },
      },
    },
  },
});

// Light theme variant
export const lightTheme = createTheme({
  ...theme,
  palette: {
    mode: 'light',
    primary: {
      main: colors.coralRed,
      dark: colors.coralRedDark,
      light: colors.lightCoral,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: colors.tealDark,
      light: colors.tealLight,
      dark: colors.mutedTealDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: colors.lightBackground,
      paper: '#FFFFFF',
    },
    text: {
      primary: colors.textDark,
      secondary: 'rgba(0, 0, 0, 0.7)',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  },
  components: {
    ...theme.components,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: colors.textDark,
          borderBottom: `1px solid ${colors.lightGrey}`,
        },
      },
    },
  },
});